// ── Types Paramétrage Organisation RH ──────────────────────────────────────

// ─── Organigramme ───────────────────────────────────────────────────────────

export interface Direction {
  id: string;
  code: string;
  libelle: string;
  responsable?: string;
  description?: string;
  /**
   * Direction parente (rattachement hiérarchique inter-directions).
   * `null` (ou absent) = direction racine ; sinon = id de la direction parente.
   */
  directionParenteId?: string | null;
  actif: boolean;
}

export type TypeRattachement = 'HIERARCHIQUE' | 'STAFF' | 'FONCTIONNEL';

export interface Departement {
  id: string;
  code: string;
  libelle: string;
  directionId: string;
  responsable?: string;
  description?: string;
  /**
   * Mode de rattachement du département à sa direction.
   * - HIERARCHIQUE (défaut) : lien vertical classique dans l'arbre
   * - STAFF : affiché latéralement à côté du nœud direction (rôle conseil/support)
   * - FONCTIONNEL : lien fonctionnel en pointillé
   */
  typeRattachement?: TypeRattachement;
  actif: boolean;
}

export interface Service {
  id: string;
  code: string;
  libelle: string;
  departementId: string;
  responsable?: string;
  description?: string;
  actif: boolean;
}

// ─── Classification ─────────────────────────────────────────────────────────

export interface Categorie {
  id: string;
  code: string;
  libelle: string;
  description?: string;
  actif: boolean;
}

export interface Fonction {
  id: string;
  code: string;
  libelle: string;
  description?: string;
  niveauHierarchique: 1 | 2 | 3 | 4 | 5;
  actif: boolean;
}

// ─── Fiche de poste — structures détaillées ─────────────────────────────────

export interface QualificationsRequises {
  niveauEtudes: string;          // "Bac+5", "BTS", "Licence"…
  diplomes: string[];            // ["Master Finance", "DSCG"]
  experienceAns: number;         // Années minimum
  typeExperience?: string;       // Description du type d'expérience
  langues: string[];             // ["Français courant", "Anglais professionnel"]
  logiciels: string[];           // ["Excel", "SAP", "Sage Paie"]
  formations: string[];          // ["Formation OHADA", "Habilitation sécurité"]
}

export interface RelationsTravail {
  superieurDirectPosteId?: string;   // ID du poste supérieur hiérarchique
  superieurDirect?: string;          // Libellé libre si pas de poste paramétré
  subordonnésDirectsIds: string[];   // IDs des postes subordonnés
  relationsInternes: string[];       // ["Service Finance", "Direction Commerciale"]
  relationsExternes: string[];       // ["Clients", "Fournisseurs", "Administration"]
  nombreSubordonnes?: number;
}

export interface ConditionsTravail {
  lieu?: string;                     // "Siège Abidjan Plateau"
  horaires?: string;                 // "Lundi-Vendredi 8h-17h"
  deplacements: boolean;
  frequenceDeplacements?: string;    // "Fréquents (>50% du temps)", "Occasionnels"
  teletravail: boolean;
  equipements: string[];             // ["Véhicule de service", "Téléphone"]
  risquesProfessionnels?: string;
  tenuRequise?: string;
}

export interface KPIPoste {
  id: string;
  libelle: string;               // "Taux de recouvrement"
  description?: string;
  cible?: string;                // "> 95%"
  periodicite?: string;          // "Mensuel", "Trimestriel"
}

// ─── Poste (étendu) ─────────────────────────────────────────────────────────

export interface Poste {
  id: string;
  code: string;
  libelle: string;
  fonctionId: string;
  categorieId: string;
  directionId?: string;
  departementId?: string;
  serviceId?: string;

  // ── Identification & objet ──
  objetPoste?: string;               // Résumé / raison d'être du poste (1-3 lignes)

  // ── Activités ──
  missionsClefs: string[];           // 3-5 missions principales
  activitesDetaillees: string[];     // Liste détaillée des tâches

  // ── Profil ──
  competencesRequises: string[];     // Compétences techniques / savoir-faire
  competencesComportementales: string[]; // Soft skills
  qualifications: QualificationsRequises;

  // ── Organisation ──
  relationsTravail: RelationsTravail;
  conditionsTravail: ConditionsTravail;

  // ── Rémunération ──
  avantageIds: string[];
  grilleSalaireId?: string;
  salaireMin?: number;
  salaireMax?: number;

  // ── Indicateurs de performance ──
  kpis: KPIPoste[];

  // ── Métadonnées fiche ──
  dateCreation?: string;
  dateRevision?: string;
  validePar?: string;
  version?: string;                  // "v1.0", "v2.3"

  actif: boolean;
}

// ─── Grille de salaire ──────────────────────────────────────────────────────

export type PeriodicitePrime   = 'mensuelle' | 'trimestrielle' | 'semestrielle' | 'annuelle' | 'exceptionnelle';
export type StatutGrille       = 'active' | 'archivee' | 'projet';
export type TypeRemuneration   = 'mensuel' | 'journalier';  // mensuel = salaire fixe, journalier = taux/jour

// Régime social/fiscal de la grille — détermine le mode de calcul net ↔ brut.
//   'salarie'             : cotisations salariées CI (CNPS, CMU, IS, CN, IGR)
//   'gerant_majoritaire'  : mandataire social non salarié (barème IRG + CN 1,5%)
export type RegimeGrille       = 'salarie' | 'gerant_majoritaire';

export interface Prime {
  id: string;
  libelle: string;             // "Prime de rendement", "Prime de présence"
  montant: number;
  periodicite: PeriodicitePrime;
  description?: string;
}

// ── Cotisation interne paramétrée sur la grille ─────────────────────────────
export interface CotisationInterneGrille {
  id: string;
  libelle: string;             // "Mutuelle", "Syndicat", "Caisse de solidarité"
  type: 'fixe' | 'pourcentage';
  valeur: number;              // Montant fixe (FCFA) ou taux (%)
  base?: 'brut' | 'base';     // Si pourcentage : assiette de calcul
}

export interface EchelonSalaire {
  niveau: string;
  echelon: string;
  coefficient: number;

  // ── Mode mensuel ──
  salaireBase: number;
  sursalaire?: number;                  // Complément salarial fixe (ancienneté, technicité…)
  indemniteTransport?: number;
  indemniteLogement?: number;

  // ── Mode journalier ──
  tauxJournalier?: number;              // Taux de base par jour travaillé
  sursalaireJournalier?: number;        // Supplément journalier (nuit, pénibilité…)
  indemniteTransportJournaliere?: number; // Allocation transport par jour
  heuresParJour?: number;               // Nombre d'heures par jour (défaut : 8)

  primesSpecifiques?: Prime[];          // Primes propres à cet échelon
  cotisationsInternes?: CotisationInterneGrille[]; // Retenues internes propres à cet échelon
}

export interface GrilleSalaire {
  id: string;
  libelle: string;
  description?: string;
  categorieId?: string;
  echelons: EchelonSalaire[];

  // ── Type de rémunération ──
  typeRemuneration: TypeRemuneration;  // 'mensuel' | 'journalier'
  joursParMois?: number;               // Nombre de jours ouvrables/mois (défaut : 26)

  // ── Régime social/fiscal ──
  // Détermine le calcul d'inversion net → brut à la saisie des échelons.
  // Défaut 'salarie' (rétro-compatible avec les grilles existantes).
  regime?: RegimeGrille;

  // ── Historisation ──
  statut: StatutGrille;          // 'active' | 'archivee' | 'projet'
  dateEffet?: string;            // ISO date — prise d'effet
  dateFinValidite?: string;      // ISO date — fin de validité (renseignée à l'archivage)
  grilleRemplaceeId?: string;    // ID de la grille remplacée (traçabilité)

  // ── Primes globales (s'appliquent à tous les échelons) ──
  primesGlobales?: Prime[];

  // ── Cotisations globales (s'appliquent à tous les échelons de la grille) ──
  cotisationsGlobales?: CotisationInterneGrille[];

  actif: boolean;
}

// ─── Arbre organisationnel ──────────────────────────────────────────────────

export interface NoeudOrganisation {
  direction: Direction;
  departements: Array<{
    departement: Departement;
    services: Service[];
  }>;
}

// ─── Données stockées ───────────────────────────────────────────────────────

export interface OrganisationData {
  directions:    Direction[];
  departements:  Departement[];
  services:      Service[];
  categories:    Categorie[];
  fonctions:     Fonction[];
  postes:        Poste[];
  grilles:       GrilleSalaire[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

export const posteVide = (): Omit<Poste, 'id'> => ({
  code: '',
  libelle: '',
  fonctionId: '',
  categorieId: '',
  objetPoste: '',
  missionsClefs: [],
  activitesDetaillees: [],
  competencesRequises: [],
  competencesComportementales: [],
  qualifications: {
    niveauEtudes: '',
    diplomes: [],
    experienceAns: 0,
    langues: [],
    logiciels: [],
    formations: [],
  },
  relationsTravail: {
    subordonnésDirectsIds: [],
    relationsInternes: [],
    relationsExternes: [],
  },
  conditionsTravail: {
    deplacements: false,
    teletravail: false,
    equipements: [],
  },
  avantageIds: [],
  kpis: [],
  actif: true,
});
